import React, { useState } from 'react'; // Optimized for build
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight, ArrowDownLeft, Clock, RefreshCw, Share2 } from 'react-feather';
import { FaBitcoin } from 'react-icons/fa';

import { PROFILE_DATA } from '../accountData';
import * as S from './styles';
import { ACCOUNT_DETAILS, MOCK_TRANSACTIONS, MOCK_TRANSACTIONS_EUR } from './data';
import { SummarySection } from './components/MobileComponents';
import FedwireInstructionsModal from '../FedwireInstructionsModal';

const ProfileScreen = ({ mode = 'financial' }) => {
  const navigate = useNavigate();
  const [showFedwireModal, setShowFedwireModal] = useState(false);
  const [activeAccountId, setActiveAccountId] = useState(2); // Default to USD (id: 2)

  const data = PROFILE_DATA[mode];
  const details = ACCOUNT_DETAILS[mode];

  const fiatAccounts = data.fiatAccounts.map(acc => ({
    ...acc,
    link: `/${mode}/accounts`,
  }));

  // Dynamically resolve active account settings
  const activeAccount = fiatAccounts.find(acc => acc.id === activeAccountId) || fiatAccounts[0];
  const activeCurrency = activeAccountId === 2 ? 'USD' : 'EUR';
  const currentDetails = details[activeCurrency];
  const currentBalance = activeAccount?.balance;

  // Dynanically filter transactions based on active currency account
  const activeTransactions = activeCurrency === 'EUR' ? MOCK_TRANSACTIONS_EUR : MOCK_TRANSACTIONS;

  return (
    <S.ProfileOuterContainer>

      {/* ── MOBILE LAYOUT ── */}
      <S.MobileOnly className="manager-scroll-view">
        <S.HomeContainer>
          <S.HomeHeader>
            <S.GreetingText>
              <h1>{data.greeting}</h1>
              <p>Welcome back</p>
            </S.GreetingText>
          </S.HomeHeader>

          <S.BalanceCard>
            <S.BalanceLabel>Available USD Balance</S.BalanceLabel>
            <S.TotalBalance>{data.totalBalance}</S.TotalBalance>
            <S.AccountInfoRow>
              <S.AccountInfoItem>
                <span>Account Number</span>
                <p>{details.USD.account}</p>
              </S.AccountInfoItem>
              <S.AccountInfoItem style={{ textAlign: 'right' }}>
                <span>Routing Number</span>
                <p>{details.USD.routing}</p>
              </S.AccountInfoItem>
            </S.AccountInfoRow>
            <S.ReceiveMoneyMobileBtn onClick={() => setShowFedwireModal(true)}>
              <Share2 size={14} style={{ marginRight: '6px' }} />
              Instructions to Receive Money
            </S.ReceiveMoneyMobileBtn>
          </S.BalanceCard>

          <SummarySection title="Fiat Accounts" items={fiatAccounts} mode={mode} />

          <S.SectionTitle>Recent Transactions</S.SectionTitle>
          <S.TxListContainer>
            {MOCK_TRANSACTIONS.slice(0, 4).map((tx, i) => (
              <S.TxRowMob key={i}>
                <S.TxInfoMob>
                  <S.TxDescMob>{tx.desc}</S.TxDescMob>
                  <S.TxDateMob>{tx.date}</S.TxDateMob>
                </S.TxInfoMob>
                <S.TxAmountMob $isDebit={tx.isDebit}>{tx.amount}</S.TxAmountMob>
              </S.TxRowMob>
            ))}
            <S.ViewMoreBtn onClick={() => navigate(`/${mode}/accounts`, { state: { myAccountTab: 'txHistory' } })}>
              View More
            </S.ViewMoreBtn>
          </S.TxListContainer>
        </S.HomeContainer>
      </S.MobileOnly>

      {/* ── DESKTOP DASHBOARD ── */}
      <S.DesktopDashboard>
        {/* HEADER SECTION */}
        <S.DesktopHeader>
          <S.DesktopGreeting>
            <h1>{data.greeting}</h1>
            <p>Welcome back. Here is your unified treasury overview today.</p>
          </S.DesktopGreeting>
        </S.DesktopHeader>

        {/* LEFT COLUMN (MAIN VIEW) */}
        <S.DesktopMainColumn>
          <S.AccountPillRow>
            <S.AccountPill 
              $active={activeAccountId === 2} 
              onClick={() => setActiveAccountId(2)}
            >
              <span className="dot" />
              🇺🇸 USD Account
            </S.AccountPill>
            <S.AccountPill 
              $active={activeAccountId === 1} 
              onClick={() => setActiveAccountId(1)}
            >
              <span className="dot" />
              🇪🇺 EUR Account
            </S.AccountPill>
          </S.AccountPillRow>

          {/* Main Dynamic Overview Card */}
          <S.HeroUSDCard>
            <S.HeroUSDLeft>
              <S.HeroTitle>Available {activeCurrency} Balance</S.HeroTitle>
              <S.HeroAmount>{currentBalance}</S.HeroAmount>
              <S.HeroAccountDetails>
                {activeCurrency === 'USD' ? (
                  <>
                    <div>
                      <span className="label">Account Number</span>
                      <span className="value">{currentDetails.account}</span>
                    </div>
                    <div>
                      <span className="label">Routing Number</span>
                      <span className="value">{currentDetails.routing}</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <span className="label">IBAN</span>
                      <span className="value">{currentDetails.iban}</span>
                    </div>
                    <div>
                      <span className="label">SWIFT / BIC</span>
                      <span className="value">{currentDetails.bic}</span>
                    </div>
                  </>
                )}
                <div>
                  <span className="label">Account Type</span>
                  <span className="value">{currentDetails.type}</span>
                </div>
              </S.HeroAccountDetails>
            </S.HeroUSDLeft>
            <S.HeroUSDRight>
              <S.HeroInstructionsBtn onClick={() => setShowFedwireModal(true)}>
                <Share2 size={13} style={{ marginRight: '8px' }} />
                Get Wire &amp; ACH Instructions
              </S.HeroInstructionsBtn>
            </S.HeroUSDRight>
          </S.HeroUSDCard>
        </S.DesktopMainColumn>

        {/* RIGHT COLUMN (SIDEBAR) */}
        <S.DesktopSidebarColumn>
          {/* Quick Actions */}
          <S.SidebarPanelCard>
            <S.PanelTitle>Quick Actions</S.PanelTitle>
            <S.DesktopQAGrid>
              <S.QACard onClick={() => navigate(`/${mode}/transfer/domestic`)} data-testid="qa-send-domestic">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <S.QAIconCircle className="qa-icon-circle">
                    <ArrowUpRight size={14} />
                  </S.QAIconCircle>
                  <S.QAText>
                    <S.QALabel>Send Domestic</S.QALabel>
                    <S.QASub>ACH, Fedwire transfer</S.QASub>
                  </S.QAText>
                </div>
                <S.QAArrow className="qa-arrow">→</S.QAArrow>
              </S.QACard>

              <S.QACard onClick={() => navigate(`/${mode}/transfer/international`)} data-testid="qa-send-international">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <S.QAIconCircle className="qa-icon-circle">
                    <ArrowUpRight size={14} />
                  </S.QAIconCircle>
                  <S.QAText>
                    <S.QALabel>Send International</S.QALabel>
                    <S.QASub>SWIFT, FX wire transfer</S.QASub>
                  </S.QAText>
                </div>
                <S.QAArrow className="qa-arrow">→</S.QAArrow>
              </S.QACard>

              <S.QACard onClick={() => navigate(`/${mode}/fx`)} data-testid="qa-fx-convert" style={{ opacity: 0.6, cursor: 'default' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <S.QAIconCircle className="qa-icon-circle">
                    <RefreshCw size={12} />
                  </S.QAIconCircle>
                  <S.QAText>
                    <S.QALabel>FX Convert</S.QALabel>
                    <S.QASub>Spot &amp; forward liquidity</S.QASub>
                  </S.QAText>
                </div>
                <span style={{ fontSize: '9px', fontWeight: '700', color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '3px 8px', borderRadius: '4px', letterSpacing: '0.5px' }}>SOON</span>
              </S.QACard>

              <S.QACard onClick={() => navigate(`/${mode}/crypto`)} data-testid="qa-crypto-otc" style={{ opacity: 0.6, cursor: 'default' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <S.QAIconCircle className="qa-icon-circle">
                    <FaBitcoin size={14} />
                  </S.QAIconCircle>
                  <S.QAText>
                    <S.QALabel>Crypto OTC</S.QALabel>
                    <S.QASub>RFQ &amp; settlement desk</S.QASub>
                  </S.QAText>
                </div>
                <span style={{ fontSize: '9px', fontWeight: '700', color: '#f59e0b', background: 'rgba(245,158,11,0.1)', padding: '3px 8px', borderRadius: '4px', letterSpacing: '0.5px' }}>SOON</span>
              </S.QACard>
            </S.DesktopQAGrid>
          </S.SidebarPanelCard>
        </S.DesktopSidebarColumn>

        {/* FULL WIDTH BOTTOM SECTION */}
        {/* Dynamic Transaction History */}
        <S.TransactionsCard>
          <S.PanelTitle>{activeCurrency} Transaction History</S.PanelTitle>
          <S.TxTable>
            <S.TxTableHead>
              <S.TxTableHeadCell /> {/* Icon column */}
              <S.TxTableHeadCell>Date</S.TxTableHeadCell>
              <S.TxTableHeadCell>Description</S.TxTableHeadCell>
              <S.TxTableHeadCell>Type</S.TxTableHeadCell>
              <S.TxTableHeadCell>Amount</S.TxTableHeadCell>
              <S.TxTableHeadCell>Status</S.TxTableHeadCell>
            </S.TxTableHead>
            {activeTransactions.slice(0, 5).map((tx, i) => (
              <S.TxTableRow key={i}>
                <S.TxIconCircle $isDebit={tx.isDebit}>
                  {tx.status === 'Pending' ? (
                    <Clock size={11} />
                  ) : tx.isDebit ? (
                    <ArrowUpRight size={11} />
                  ) : (
                    <ArrowDownLeft size={11} />
                  )}
                </S.TxIconCircle>
                <S.TxCell color="#9ca3af">{tx.date}</S.TxCell>
                <S.TxCell>{tx.desc}</S.TxCell>
                <S.TxCell color={tx.isDebit ? '#ef4444' : '#374151'} bold>{tx.type}</S.TxCell>
                <S.TxCell color={tx.isDebit ? '#ef4444' : '#059669'} bold>{tx.amount}</S.TxCell>
                <S.TxCell>
                  <S.TxBadge
                    green={tx.status === 'Received' || tx.status === 'Processed' || tx.status === 'Cleared'}
                    yellow={tx.status === 'Pending'}
                  >
                    {tx.status}
                  </S.TxBadge>
                </S.TxCell>
              </S.TxTableRow>
            ))}
          </S.TxTable>
          <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'center' }}>
            <S.DesktopViewMoreBtn onClick={() => navigate(`/${mode}/accounts`, { state: { myAccountTab: 'txHistory' } })}>
              View More
            </S.DesktopViewMoreBtn>
          </div>
        </S.TransactionsCard>
      </S.DesktopDashboard>

      <FedwireInstructionsModal 
        show={showFedwireModal} 
        onClose={() => setShowFedwireModal(false)} 
        account={{ accountNumber: activeCurrency === 'USD' ? details.USD.account : details.EUR.iban }}
      />

    </S.ProfileOuterContainer>
  );
};

export default ProfileScreen;
